import { createFileRoute } from "@tanstack/react-router";
import { CarouselStandard } from "~/components/3_organisms/CarouselStandard";
import { AlternatingShowcase } from "~/components/4_modules/AlternatingShowcase";
import { Hero } from "~/components/4_modules/Hero";
import { ImageBanner } from "~/components/4_modules/ImageBanner";
import StandardSection from "~/components/4_modules/StandardSection";
import { SEO } from "~/components/SEO";
import { localBusinessSchema } from "~/lib/structured-data";

// Restaurant carousel images
const restaurantCarouselImages = [
  { src: "/images/restaurant-carousel/01.webp", alt: "Restaurant Image 1" },
  { src: "/images/restaurant-carousel/02.webp", alt: "Restaurant Image 2" },
  { src: "/images/restaurant-carousel/03.webp", alt: "Restaurant Image 3" },
  { src: "/images/restaurant-carousel/04.webp", alt: "Restaurant Image 4" },
  { src: "/images/restaurant-carousel/05.webp", alt: "Restaurant Image 5" },
  { src: "/images/restaurant-carousel/06.webp", alt: "Restaurant Image 6" },
  { src: "/images/restaurant-carousel/07.webp", alt: "Restaurant Image 7" },
  { src: "/images/restaurant-carousel/08.webp", alt: "Restaurant Image 8" },
  { src: "/images/restaurant-carousel/09.webp", alt: "Restaurant Image 9" },
  { src: "/images/restaurant-carousel/10.webp", alt: "Restaurant Image 10" },
  { src: "/images/restaurant-carousel/11.webp", alt: "Restaurant Image 11" },
];

const restaurantContent = {
  hero: {
    backgroundImage: "/images/restaurant-hero.webp",
    title: "Il ristorante",
  },
  intro: {
    title: (
      <span>
        Un viaggio tra sapori <br className="block xl:hidden"></br>e profumi
      </span>
    ),
    description: (
      <>
        <span className="inline-block">
          In una terra rinomata per i suoi piatti di montagna, abbiamo scelto di proporre
          una cucina prevalentemente di pesce.
        </span>
        <span className="mt-4 inline-block">
          Selezioniamo quotidianamente materie prime freschissime, trasformandole in
          piatti che esaltano i sapori del mare con creatività e rispetto per la
          tradizione.
        </span>
      </>
    ),
    image: {
      src: "/images/restaurant-01.webp",
      alt: "Image of a dessert served in a glass",
      alignment: "bottom",
    },
  },
  pizzeria: {
    title: "La pizzeria",
    description: (
      <>
        <span className="inline-block">
          La nostra pizza è frutto di mesi di studio e sperimentazione, la nostra pizza si
          distingue per un impasto a lunga lievitazione e una cottura perfetta.
        </span>
        <span className="mt-4 inline-block">
          Utilizziamo solo ingredienti freschi e di alta qualità, offrendo un’esperienza
          gastronomica che soddisfa anche i palati più esigenti.
        </span>
      </>
    ),
  },
  menuIntro: {
    paragraph: [
      "Il nostro menù è un invito a scoprire piatti che raccontano storie di mare e di passione culinaria. Dalle tartare freschissime ai risotti profumati, ogni portata è pensata per deliziare i sensi.",
    ],
  },
  menuDesktop: {
    title: "Menù",
    description: (
      <div className="space-y-6">
        <div className="flex w-full items-center justify-between">
          <h3 className="tg-primary">Il nostro misto di mare</h3>
          <p className="tg-primary">€ 22,00</p>
        </div>
        <div className="flex w-full items-center justify-between">
          <h3 className="tg-primary">Tagliolini allo scoglio</h3>
          <p className="tg-primary">€ 15,00</p>
        </div>
        <div className="flex w-full items-center justify-between">
          <h3 className="tg-primary">Spaghettoni alle vongole</h3>
          <p className="tg-primary">€ 15,00</p>
        </div>
        <div className="flex w-full items-center justify-between">
          <h3 className="tg-primary">Fritto misto</h3>
          <p className="tg-primary">€ 20,00</p>
        </div>
        <div className="flex w-full items-center justify-between">
          <h3 className="tg-primary">Grigliata di pesce</h3>
          <p className="tg-primary">€ 22,00</p>
        </div>
      </div>
    ),
  },
  menuMobile: {
    title: "Menù",
    description: (
      <>
        <p>
          Il nostro menù è un invito a scoprire piatti che raccontano storie di mare e di
          passione culinaria. Dalle tartare freschissime ai risotti profumati, ogni
          portata è pensata per deliziare i sensi
        </p>
      </>
    ),
  },
};

export const Route = createFileRoute("/restaurant")({
  component: Restaurant,
});

function Restaurant() {
  return (
    <>
      <SEO
        title="Il Ristorante"
        socialTitle="Ristorante Tennis & Gourmet Morbegno | Il Ristorante"
        description="Ristorante Morbegno specializzato in cucina di pesce e pizzeria gourmet. Nel cuore della Valtellina, Tennis Gourmet offre piatti freschi e creativi con materie prime selezionate quotidianamente."
        keywords="ristorante morbegno, ristorante valtellina, ristorante pesce morbegno, cucina mare valtellina, pizzeria gourmet morbegno, ristorante pesce valtellina"
        url="https://www.ristorantetennisgourmet.it/restaurant"
        image="/images/restaurant-hero.webp"
        type="restaurant"
        structuredData={localBusinessSchema}
      />
      {/* Hero Section */}
      <Hero backgroundImage={restaurantContent.hero.backgroundImage}>
        <h2 className="tg-h1 font-medium uppercase">{restaurantContent.hero.title}</h2>
      </Hero>
      {/* intro Section */}
      <AlternatingShowcase
        items={[
          {
            title: restaurantContent.intro.title,
            description: restaurantContent.intro.description,
            id: "restaurant-section",
            image: {
              src: "/images/restaurant-01.webp",
              alt: "Image of a dessert served in a glass",
              alignment: "bottom",
            },
            backgroundStyle: "pattern",
            decorPattern: (
              <img
                src="/images/decor/decor-bigL.webp"
                alt="page decoration"
                className="3xl:w-auto 3xl:h-[85%] 3xl:-left-1/6 absolute -bottom-0 -left-6 h-auto w-[90vw] object-cover object-bottom md:-left-21 xl:w-[50vw] 2xl:-left-60"
              />
            ),
          },
        ]}
      />

      {/* Restaurant Carousel */}
      <CarouselStandard
        images={restaurantCarouselImages}
        className="py-12 lg:py-16 xl:py-8"
      />

      {/* Pizzeria Section */}
      <AlternatingShowcase
        items={[
          {
            id: "pizzeria",
            title: restaurantContent.pizzeria.title,
            description: restaurantContent.pizzeria.description,
            imagePosition: "right",
            image: {
              src: "/images/restaurant-02.webp",
              alt: "Image of the prepared pizza",
              alignment: "bottom",
            },
            backgroundStyle: "none",
          },
        ]}
      />
      {/* Image Banner */}
      <ImageBanner
        src={{
          mobile: "/images/banner-restaurant--mobile.webp",
          desktop: "/images/banner-restaurant.webp",
        }}
        alt="Image of a dessert served in a glass"
      />
      <StandardSection
        sectionClass="hidden xl:block"
        paragraph={restaurantContent.menuIntro.paragraph}
      ></StandardSection>
      {/* Menu */}
      <AlternatingShowcase
        items={[
          {
            id: "menu",
            title: restaurantContent.menuDesktop.title,
            description: restaurantContent.menuDesktop.description,
            className: "hidden xl:grid pt-0",
            textColumnClasses: "xl:col-end-8",
            image: {
              src: "/images/restaurant-03.webp",
              alt: "Image of a dish",
              alignment: "bottom",
              mobileHide: true,
            },
            backgroundStyle: "none",
            cta: {
              text: "Scopri il menù",
              variant: "default",
              onClick: () => window.open("/menu_tennisgourmet.pdf", "_blank"),
            },
          },
        ]}
      />
      <AlternatingShowcase
        items={[
          {
            id: "menu",
            title: restaurantContent.menuMobile.title,
            description: restaurantContent.menuMobile.description,
            className: "xl:hidden",
            image: {
              src: "/images/restaurant-03.webp",
              alt: "Image of a dish",
              alignment: "bottom",
              mobileHide: true,
            },
            backgroundStyle: "none",
            cta: {
              text: "Scopri il menù",
              variant: "default",
              onClick: () => window.open("/menu_tennisgourmet.pdf", "_blank"),
            },
          },
        ]}
      />
    </>
  );
}
